import {
  Card,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { PlanGenerationAttemptOut } from "@providers/hop-ord-server/api";
import { toLocaleDateTimeString } from "@utils";

interface Props {
  plans: PlanGenerationAttemptOut[];
}

// Export function to get the same timestamps used by CompletedPlans
export const getCompletedPlanTimestamps = () => {
  const storageKey = "completedPlansTimestamps";
  const stored = localStorage.getItem(storageKey);

  if (stored) {
    // Use existing timestamps
    const timestamps = JSON.parse(stored);
    return timestamps;
  } else {
    // Generate new timestamps based on current time
    const now = Date.now();
    const timestamps = {
      plan1FinishedAt: new Date(now - 10 * 60 * 1000).toISOString(), // 10 minutes ago (Prostate Nodes R1)
      plan1StartedAt: new Date(now - 15 * 60 * 1000).toISOString(), // 15 minutes ago
      plan2FinishedAt: new Date(now).toISOString(), // Now (Prostate Nodes R2)
      plan2StartedAt: new Date(now - 5 * 60 * 1000).toISOString(), // 5 minutes ago
    };

    // Store for future use
    localStorage.setItem(storageKey, JSON.stringify(timestamps));
    return timestamps;
  }
};

const CompletedPlans = ({ plans: _plans }: Props) => {
  // Function to get or generate persistent timestamps
  const getPersistentTimestamps = () => {
    return getCompletedPlanTimestamps();
  };

  const timestamps = getPersistentTimestamps();

  // Mock data for demonstration
  const mockPlans = [
    {
      templateName: "Prostate Nodes R1",
      warnings: [],
      error: null,
      relatedPrescription: "Prostate + Nodes VMAT 7000-5040/28",
      machine: null,
      finishedAt: timestamps.plan1FinishedAt,
      planningStartedAt: timestamps.plan1StartedAt,
      currentStep: "completed",
      stepSequence: ["planning", "optimization", "validation"],
      planName: "Prostate Nodes R1",
      id: "plan-001",
      status: "completed",
      progress: 100,
    },
    {
      templateName: "Prostate Nodes R2",
      warnings: [],
      error: null,
      relatedPrescription: "Prostate + Nodes VMAT 7000-5040/28",
      machine: null,
      finishedAt: timestamps.plan2FinishedAt,
      planningStartedAt: timestamps.plan2StartedAt,
      currentStep: "completed",
      stepSequence: ["planning", "optimization", "validation"],
      planName: "Prostate Nodes R2",
      id: "plan-002",
      status: "completed",
      progress: 100,
    },
  ];

  const displayPlans = mockPlans;

  return (
    <Card sx={{ padding: 2 }}>
      <Typography variant="h6">Completed Plans</Typography>
      <TableContainer sx={{ maxHeight: "400px" }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Plan Name</TableCell>
              <TableCell>Related prescription</TableCell>
              <TableCell>Generated</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {displayPlans?.map((plan, index) => {
              const showChips = plan?.warnings?.length > 0 || plan?.error;
              return (
                <TableRow key={index}>
                  <TableCell>
                    <Stack gap={1}>
                      <Typography variant="body2">
                        {plan.templateName}
                      </Typography>
                      {showChips && (
                        <Stack direction="row" alignItems="center" gap={1}>
                          {plan?.warnings?.length > 0 && (
                            <Chip
                              size="small"
                              variant="outlined"
                              label={`${plan.warnings.length} warnings`}
                              color="warning"
                            />
                          )}
                        </Stack>
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Stack>
                      <Typography variant="body2">
                        {plan?.relatedPrescription || "-"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {plan.machine}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    {toLocaleDateTimeString(plan?.finishedAt || "")}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  );
};

export default CompletedPlans;
